/**
 * Retry a join the server turned away because signaling was too busy to mint
 * a room token.
 *
 * The backend used to hand out an empty token instead, and the client walked
 * into the meeting with a signaling socket that could never connect. It now
 * answers 503 `SIGNALING_UNAVAILABLE` (backend `platform/signaling`), which is
 * almost always a busy moment rather than an outage — so the join is retried
 * twice, spread out, before the caller sees the error.
 *
 * Matched on the code alone, without importing `ApiError`: this module is
 * imported BY meetings.api, and the code is the contract anyway.
 */

export const SIGNALING_BUSY_CODE = 'SIGNALING_UNAVAILABLE'
export const SIGNALING_BUSY_DELAYS_MS: readonly number[] = [2_000, 4_000]

export async function retryWhileSignalingBusy<T>(
  attempt: () => Promise<T>,
  opts: {
    delaysMs?: readonly number[]
    sleep?: (ms: number) => Promise<void>
    random?: () => number
  } = {},
): Promise<T> {
  const delays = opts.delaysMs ?? SIGNALING_BUSY_DELAYS_MS
  const sleep = opts.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)))
  const random = opts.random ?? Math.random
  for (let i = 0; ; i++) {
    try {
      return await attempt()
    } catch (err) {
      const busy = (err as { code?: unknown } | null)?.code === SIGNALING_BUSY_CODE
      if (!busy || i >= delays.length) throw err
      // Jittered: everyone refused in the same busy second must not all come
      // back in the same second.
      await sleep(delays[i] / 2 + random() * delays[i])
    }
  }
}
